import styled from '@emotion/styled'
import { useAuth } from '@bty/react-auth'
import { useRequest } from 'ahooks'
import { Spin, Tooltip, Divider, message } from 'antd'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { IEmbeddingModalItem } from '@/apis/datastore/api.ts'
import {
  fetchEmbeddingModelList,
  getDataStoreList,
  getAssociatedApplication,
} from '@/apis/datastore/api.ts'
import { AppLogo, Button, IconFont, Input } from '@/components'
import { colors } from '@/constants/theme'
import {
  CreateDataStoreDatasetModal,
  getPartitionAvatarInfo,
} from '@/features/datastore/CreateDataStoreModal/DatasetModal'
import { useDataStoreCreate } from '@/features/datastore/hooks/useDataStoreCreate.ts'
import { useLimitedModal } from '@/features/pay'
import { useModal } from '@/hooks/useModal.tsx'
import { useVersionStore, useWorkspaceStore } from '@/store'
import {
  ResponsiveCard,
  ResponsiveCardGrid,
} from '@/components/list/ResponsiveCardGrid.tsx'
import { useScrollBar } from '@/hooks/useScrollBar.ts'
import { PrivateDeployGuard } from '@/features/guard/PrivateDeployGuard.tsx'
import { AppDropDown } from '../agent/components/AppDropDown'
import {
  useWorkspaceSelectModal,
  SelectType,
} from '../application/components/WorkSpaceSelect'
import { forkDataSet } from '@/apis/application'
import { PartitionCategoryType } from '@/apis/datastore/types'
import { LimitedAlertBar } from '@/features/pay/LimitedAlert'
import { CreateDataStoreDocumentsModal } from '@/features/datastore/CreateDataStoreModal/DocumentsModal'
import { CreateType } from '@/features/datastore/constant'
import { CreateDocumentsButton } from '@/features/datastore/components/CreateDocumentsButton'
import { CreateDataStoreCard } from './CreateDataStoreCard'

const DatastoreListWrapper = styled.div`
  .scroll-content {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`

const DataStoreCard = styled(ResponsiveCard)`
  .ant-btn {
    border: none;
    font-size: 20px !important;
    visibility: hidden;
    .c-font_1 {
      color: #17171d !important;
    }
  }
  &:hover {
    box-shadow: 0px 12px 20px 0px rgba(23, 23, 29, 0.12);
    .ant-btn {
      visibility: visible;
    }
  }
`
function DataStoreList() {
  const navigate = useNavigate()
  const [searchName, setSearchName] = useState('')
  const versionInfo = useVersionStore(state => state.versionInfo)
  const currentWorkspaceId = useWorkspaceStore(
    state => state.currentWorkspaceId,
  )

  const { data: relateAppData, runAsync: fetchAssociatedApp } = useRequest(
    getAssociatedApplication,
    {
      manual: true,
    },
  )
  const { runAsync: forkDataSetApi, loading: copyLoading } = useRequest(
    forkDataSet,
    {
      manual: true,
    },
  )
  const { fetchVersionByWorkspaceId } = useVersionStore()
  const [searchParams] = useSearchParams()
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const [modal] = useLimitedModal()
  const {
    state: { role },
  } = useAuth()

  const cardCommonClassName =
    'flex rounded-12px bg-white cursor-pointer h-160px'
  const {
    data: dataStoreListData,
    loading,
    runAsync: fetchDataStoreList,
  } = useRequest(getDataStoreList, {
    refreshDeps: [currentWorkspaceId],
  })

  const { data: embeddingModelList } = useRequest(fetchEmbeddingModelList)

  const embeddingModelName = useMemo(() => {
    return embeddingModelList?.reduce((re, item: IEmbeddingModalItem) => {
      re[item.model_name] = item.model_display_name
      return re
    }, {})
  }, [embeddingModelList])

  const { onCreate: onDataStoreCreateApi, loading: datastoreCreateLoading } =
    useDataStoreCreate()
  const [createDatastoreDatasetModal] = useModal(CreateDataStoreDatasetModal, {
    okButtonProps: { loading: datastoreCreateLoading },
    embeddingModelList,
  })

  const [createDataStoreDocumentsModal] = useModal(
    CreateDataStoreDocumentsModal,
    {
      okButtonProps: {
        loading: datastoreCreateLoading,
      },
    },
  )

  const dataStoreList = useMemo(() => {
    const list = dataStoreListData?.partitions || []
    if (searchName.trim()) {
      return list.filter(
        item => item.partition_name.toLowerCase().includes?.(searchName),
      )
    }
    return list
  }, [dataStoreListData?.partitions, searchName])

  const handleCreate = (
    type: PartitionCategoryType = PartitionCategoryType.Document,
    actionType?: CreateType,
  ) => {
    const isDataset = actionType !== CreateType.CREATE_PARTITION
    const name = type === PartitionCategoryType.QA ? '问答' : '文档'
    const title = isDataset ? `新建${name}知识库` : '添加文档'

    if (isDataset) {
      createDatastoreDatasetModal.open({
        title,
        defaultValue: {
          partition_name: `未命名${name}知识库${dataStoreList.length + 1}`,
          partition_category: type ?? PartitionCategoryType.Document,
        },
        onFinish: async value => {
          const res = await onDataStoreCreateApi(value)
          createDatastoreDatasetModal.close()
          navigate(
            `/datastores/${currentWorkspaceId}/${res.partition_id}/documents`,
          )
        },
      })
    } else {
      createDataStoreDocumentsModal.open({
        title,
        datastoreType: type,
        onFinish: async value => {
          if (!value) {
            return
          }
          const res = await onDataStoreCreateApi({
            ...value,
            partition: {
              ...value?.partition,
              partition_name: `未命名${name}知识库${dataStoreList.length + 1}`,
              partition_category: type,
              ...getPartitionAvatarInfo(type),
            },
          })
          createDataStoreDocumentsModal.close()
          navigate(
            `/datastores/${currentWorkspaceId}/${res.partition_id}/documents`,
          )
        },
      })
    }
  }

  const handleButtonClick = () => {
    setTooltipOpen(false)
    window.open('/chrome-plugin', '_blank')
  }
  useEffect(() => {
    fetchVersionByWorkspaceId(currentWorkspaceId)
    if (searchParams.get('create')) {
      handleCreate(PartitionCategoryType.Document)
    }
  }, [])

  const { scrollRef } = useScrollBar()

  const renderEmpty = useMemo(() => {
    return (
      <div className='h-full flex w-100% item-center flex-1'>
        <CreateDataStoreCard
          create={type => handleCreate(type, CreateType.CREATE_PARTITION)}
        />
      </div>
    )
  }, [handleCreate])
  const handleDataBaseCopy = async (
    target_workspace_id: string,
    partition_id: number,
  ) => {
    await forkDataSetApi({
      source_partition_id: partition_id?.toString() || '',
      source_workspace_id: currentWorkspaceId,
      target_workspace_id,
    })
    onForkSuccess()
    fetchDataStoreList()
    message.success('复制成功')
  }
  const [copyModal] = useWorkspaceSelectModal({
    onConfirm: handleDataBaseCopy,
    defaultWorkspaceId: currentWorkspaceId,
    copyType: '复制知识库',
    loading: copyLoading,
  })

  function onForkSuccess() {
    copyModal.close()
  }

  const renderList = useMemo(() => {
    return (
      <>
        {dataStoreList.map(item => {
          return (
            <DataStoreCard
              key={item.partition_id}
              onClick={() => {
                navigate(
                  `/datastores/${currentWorkspaceId}/${item.partition_id}/documents`,
                )
              }}
              className={classNames(
                cardCommonClassName,
                'flex-col justify-between',
              )}
            >
              <div>
                <div className='flex px-20 pt-20 flex-col'>
                  <div className='flex flex-1'>
                    <AppLogo
                      type='icon'
                      color={item.partition_icon_color ?? colors[0]}
                      value={item.partition_icon ?? 'app'}
                    />
                    <div className='ml-16 mt-2 max-w-[70%]'>
                      <p className='truncate font-500 text-16px/20px'>
                        {item.partition_name}
                      </p>
                      <div className='mt-12px flex items-center text-12px text-font_1 line-height-12px flex items-center'>
                        <p className='truncate text-12px line-height-18px text-font_1 max-h-66px overflow-hidden break-all'>
                          {item.partition_category === PartitionCategoryType.QA
                            ? '问答 · '
                            : '文档 · '}
                        </p>
                        {!!embeddingModelName?.[item.partition_model!] &&
                          !!embeddingModelList?.length && (
                            <div className='flex items-center'>
                              <div className='ml-4px'>
                                {embeddingModelName[item.partition_model!]}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                  <p className='truncate mt-12px text-12px/18px h-18px mt-8px text-font_1 max-h-66px overflow-hidden'>
                    {item.partition_describe}
                  </p>
                </div>
              </div>
              <div className=' border-opacity-12 p-20 flex justify-between'>
                <div className='flex gap-24px'>
                  <div className='text-12px text-font_1 line-height-12px flex items-center'>
                    <IconFont name='doc' className='text-10px text-font_1' />
                    <span className='mx-4'>{item.file_num}</span>
                    <span>文档</span>
                  </div>
                  <div className='text-12px text-font_1 line-height-12px flex items-center'>
                    <IconFont name='app' className='text-10px text-font_1' />
                    <span className='mx-4'>{item.application.length}</span>
                    <span>个应用</span>
                  </div>
                </div>
                <div onMouseEnter={() => fetchAssociatedApp(item.partition_id)}>
                  <AppDropDown
                    app={item}
                    selectType={SelectType.DATASTORE}
                    isRelateApp={relateAppData && relateAppData?.length > 0}
                    handleFork={() => {
                      copyModal.open(item)
                    }}
                    handleDelete={() => {
                      fetchDataStoreList()
                    }}
                  ></AppDropDown>
                </div>
              </div>
            </DataStoreCard>
          )
        })}
      </>
    )
  }, [dataStoreList, handleCreate])

  return (
    <DatastoreListWrapper className='w-full h-full flex'>
      <div className='adapt:pt-40 bg-bg rounded-12px flex-1 overflow-hidden w-full h-full flex flex-col'>
        <div className='adapt:px-64'>
          <div className='flex flex-item-center justify-between pb-24 b-b-line b-op-40 border-b-1px'>
            <div className='flex items-center h-36px flex-wrap'>
              <span className='line-height-36px font-500 text-20px nowrap'>
                知识库
              </span>
              <PrivateDeployGuard>
                <div className='pr-16px ml-12px'>
                  剩余容量：
                  {Math.max(
                    (versionInfo?.memory_count_limit || 0) -
                      (versionInfo?.memory_consume_num || 0),
                    0,
                  ).toFixed(1)}
                  MB
                  <span
                    onClick={() => modal.open({ role })}
                    className='ml-12px cursor-pointer text-14px font-500 c-primary'
                  >
                    扩容
                  </span>
                </div>
              </PrivateDeployGuard>
            </div>
            <div>
              <Input
                className='w-400px important:text-14px'
                prefix={<IconFont name='search' />}
                placeholder='搜索知识库名称'
                value={searchName}
                onChange={e => setSearchName(e.target.value)}
              />
              <Divider type='vertical' className='mx-16 h-26px' />
              <Tooltip
                className='text-14px/16px'
                open={tooltipOpen}
                onOpenChange={(open: boolean) => setTooltipOpen(open)}
                destroyTooltipOnHide
                title={
                  <span
                    style={{
                      display: 'block',
                      padding: '2px 4px',
                      lineHeight: '18px',
                    }}
                  >
                    直接从chrome浏览器 <br /> 解析网页数据
                  </span>
                }
                placement='top'
              >
                <Button
                  icon={<IconFont name='zhajian' color='#62699999' />}
                  onClick={handleButtonClick}
                >
                  知识库插件
                </Button>
              </Tooltip>
              <CreateDocumentsButton
                className='ml-12px'
                handleCreate={handleCreate}
              />
            </div>
          </div>
        </div>
        <div
          ref={scrollRef}
          className='py-20 adapt:px-64 flex-1 overflow-y-auto !h-full'
        >
          <LimitedAlertBar className='mb-20px' />
          <ResponsiveCardGrid className='content-start relative flex-grow-1'>
            <div
              className={classNames(
                'absolute top-0 left-0 w-full h-full flex-center',
                {
                  'pointer-events-none': !loading,
                },
              )}
            >
              <Spin spinning={loading} />
            </div>
            {!loading && (dataStoreList?.length ? renderList : renderEmpty)}
          </ResponsiveCardGrid>
        </div>
      </div>
    </DatastoreListWrapper>
  )
}

export default DataStoreList
